import { useCallback, useMemo, useState } from 'react';
import type { FavouriteModel, SidebarRow } from 'src/layouts/SidebarV2/types';

import { useChatActions } from './useChatActions';
import { useChatHistory } from './useChatHistory';
import { useFavourites } from './useFavourites';

export interface GroupRange {
  id: string;
  heading: string;
  collapsed: boolean;
  onToggle: () => void;
  headerIndex: number;
  endIndex: number;
}

export interface UseSidebarRows {
  rows: SidebarRow[];
  groupRanges: GroupRange[];
  hasMore: boolean;
  isFetching: boolean;
  loadMoreRows: () => void;
  onStartFavouriteChat: (item: FavouriteModel) => void;
  onToggleFavourite: (item: FavouriteModel) => void;
  onRenameChat: (id: number, title: string) => void;
  onDeleteChat: (id: number) => void;
}

export interface UseSidebarRowsOptions {
  include?: 'all' | 'history';
}

export const useSidebarRows = ({
  include = 'all'
}: UseSidebarRowsOptions = {}): UseSidebarRows => {
  const {
    groups: allFavGroups,
    toggleFavourite,
    startFavouriteChat
  } = useFavourites();
  const favGroups = useMemo(
    () => (include === 'history' ? [] : allFavGroups),
    [include, allFavGroups]
  );
  const {
    groups: historyGroups,
    hasMore,
    isFetching,
    loadMoreRows
  } = useChatHistory();
  const { renameChat, deleteChat } = useChatActions();

  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const toggle = useCallback(
    (id: string) => setCollapsed((prev) => ({ ...prev, [id]: !prev[id] })),
    []
  );

  const { rows, groupRanges } = useMemo(() => {
    const flat: SidebarRow[] = [];
    const ranges: GroupRange[] = [];

    const pushGroup = (
      groupId: string,
      heading: string,
      items: SidebarRow[]
    ) => {
      const isCollapsed = !!collapsed[groupId];
      const headerIndex = flat.length;
      flat.push({
        type: 'groupHeader',
        id: `header-${groupId}`,
        heading,
        collapsed: isCollapsed,
        onToggle: () => toggle(groupId)
      });
      if (!isCollapsed) {
        flat.push(...items);
      }
      ranges.push({
        id: groupId,
        heading,
        collapsed: isCollapsed,
        onToggle: () => toggle(groupId),
        headerIndex,
        endIndex: flat.length - 1
      });
    };

    for (const group of favGroups) {
      pushGroup(
        `fav-${group.kind}`,
        group.heading,
        group.items.map((item) => ({
          type: 'favourite' as const,
          id: item.id,
          item
        }))
      );
    }

    for (const group of historyGroups) {
      pushGroup(
        `hist-${group.id}`,
        group.heading,
        group.chats.map((chat) => ({
          type: 'chat' as const,
          id: `chat-${chat.id}`,
          chat
        }))
      );
    }

    return { rows: flat, groupRanges: ranges };
  }, [favGroups, historyGroups, collapsed, toggle]);

  return {
    rows,
    groupRanges,
    hasMore,
    isFetching,
    loadMoreRows,
    onStartFavouriteChat: startFavouriteChat,
    onToggleFavourite: toggleFavourite,
    onRenameChat: renameChat,
    onDeleteChat: deleteChat
  };
};
